import { keepPreviousData, useQuery, UseQueryResult } from '@tanstack/react-query'
import { network } from '@utils'
import { DefaultQueryParams, PaginatedResponse, QueryFunction } from './root'

export type OverviewDeadlineStatus = 'upcoming' | 'filed' | 'none'

export interface IOverviewDeadline {
  status: OverviewDeadlineStatus
  type: 'vat' | 'fiscal' | null
  date: string | null
}

export interface IOverviewCountTotal {
  count: number
  total_cents: number
}

// Column permissions are granular per business: a metric the user may see
// on one business can come back null on another. Null always means "not
// permitted here", never zero.
export interface IBusinessOverviewRow {
  id: number
  name: string
  slug: string
  business_id: string | null
  form: string
  logo: string | null
  is_agency_business: boolean
  // The viewer's usership carries the admin permission on this agency
  // business, so the archive endpoint would accept it.
  can_archive: boolean
  automation_pct: number | null
  next_deadline: IOverviewDeadline | null
  time_saved_hours: number | null
  sales?: IOverviewCountTotal | null
  purchase?: IOverviewCountTotal | null
  sales_unpaid?: IOverviewCountTotal | null
  sales_overdue?: IOverviewCountTotal | null
  purchase_overdue?: IOverviewCountTotal | null
  debt?: IOverviewCountTotal | null
  drafts?: number | null
  docs_month?: number | null
  docs_year?: number | null
  bank_count?: number | null
  last_sync_at?: string | null
  has_bank_integrations?: boolean | null
  customer_banks?: string[] | null
  luca_documents?: number | null
}

export interface IOverviewColumnOption {
  value: string
  label: string
  info: string
  coming_soon: boolean
}

export interface IOverviewFilterCounts {
  total: number
  vat_soon: number
  low_automation: number
  overdue: number
  // Present for agency users only.
  archived?: number
}

export interface BusinessOverviewParams extends DefaultQueryParams {
  ordering?: string
  columns?: string
  filters?: string
}

export const fetchBusinessOverview: QueryFunction<
  void,
  PaginatedResponse<IBusinessOverviewRow>
> = async (_, params = null) => {
  const { data } = await network.httpClient.request({
    url: '/v1/business_overview/',
    method: 'GET',
    params
  })
  return data
}

export const fetchOverviewFilterCounts: QueryFunction<void, IOverviewFilterCounts> = async () => {
  const { data } = await network.httpClient.request({
    url: '/v1/business_overview/filter_counts/',
    method: 'GET'
  })
  return data
}

export const fetchOverviewColumnOptions: QueryFunction<
  void,
  IOverviewColumnOption[]
> = async () => {
  const { data } = await network.httpClient.request({
    url: '/v1/business_overview/columns/',
    method: 'GET'
  })
  return data
}

// Agency-only bulk archive/unarchive of the selected businesses.
export const postOverviewArchive = async (payload: {
  business_ids: number[]
  archived: boolean
}): Promise<{ updated: number }> => {
  const { data } = await network.httpClient.request({
    url: '/v1/business_overview/archive/',
    method: 'POST',
    data: payload
  })
  return data
}

// The overview endpoints aggregate the whole portfolio server-side, so the
// hooks deliberately avoid focus-refetches and keep results briefly fresh —
// every avoided call is a portfolio-sized query saved.
export const useBusinessOverview = (
  params: BusinessOverviewParams,
  options?: { enabled?: boolean }
): UseQueryResult<PaginatedResponse<IBusinessOverviewRow>> =>
  useQuery({
    queryKey: ['business-overview', params],
    queryFn: () => fetchBusinessOverview(undefined, params),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    enabled: options?.enabled ?? true
  })

export const useOverviewFilterCounts = (): UseQueryResult<IOverviewFilterCounts> =>
  useQuery({
    queryKey: ['business-overview', 'filter-counts'],
    queryFn: () => fetchOverviewFilterCounts(undefined),
    staleTime: 60_000,
    refetchOnWindowFocus: false
  })

// Permission-filtered column options (constants API). Only changes when the
// user's business roles change, so it can stay fresh for a long time.
export const useOverviewColumnOptions = (): UseQueryResult<IOverviewColumnOption[]> =>
  useQuery({
    queryKey: ['business-overview', 'column-options'],
    queryFn: () => fetchOverviewColumnOptions(undefined),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false
  })
